import { Pivot, PivotItem, Spinner } from "@fluentui/react";
import useAxios from "axios-hooks";
import BasicInfoStep, { basicInfoValidation } from "./BasicInfoStep";
import AccessStep from "./AccessStep";
import AccessRolesStep from "./AccessRolesStep";
import { useCallback, useState, useEffect } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import WizardModeIndicator from '../../../../Util/WizardModeIndicator';
import { Formik } from "formik";
import ErrorComponent from "../../../../Util/ErrorComponent";
import Toast from "../../../../Forms/Toast";
import FeatureSummary from '../../../../Util/FeatureSummary';

export const ReportUsersWizardPage = () => {
    const history = useHistory();
    const { staffIdFromParams } = useParams();

    const [errorMsg, setErrorMsg] = useState("");
    const [showError, setShowError] = useState(false);
    const [showSuccessfulSaveToast, setShowSuccessfulSaveToast] = useState(false);

    const [{ data: formSubmitResponse }, submitForm] = useAxios({}, { manual: true });

    const [{}, checkEmail] = useAxios({ method: "get" }, { manual: true });

    const [{ data: reportUserData, loading: reportUserLoading }, getReportUserData] = useAxios(
        { url: `internal/reports-admin/user/${staffIdFromParams}`, method: "get" },
        { manual: true }
    );

    const staffId = (formSubmitResponse && formSubmitResponse.staffId) || staffIdFromParams;
    const [selectedKey, setSelectedKey] = useState(0);

    const nextOnClick = useCallback(() => {
        if (selectedKey == 2) return;
        setSelectedKey(Number(selectedKey) + 1);
    }, [selectedKey]);

    const prevOnClick = () => {
        setSelectedKey(Number(selectedKey) - 1);
    };

    const handleLinkClick = (item) => {
        const itemKey = item.props.itemKey;
        setSelectedKey(itemKey);
    };

    useEffect(() => {
        if (staffIdFromParams && !reportUserData) {
            getReportUserData().catch((error) => {
                setShowError(true);
                setErrorMsg(
                    `Could not load Report User data for ID ${staffIdFromParams}. Please refresh and try again.`
                );
            });
        }
    }, [reportUserData, staffIdFromParams, getReportUserData]);

    if (reportUserLoading) {
        return <Spinner className="absolute-center" text="Loading Report User..." />;
    }

    return (
        <Formik
            initialValues={mapInitialValues(reportUserData)}
            validationSchema={basicInfoValidation}
            validateOnMount={true}
            isInitialValid={!!staffId}
            onSubmit={async (values, { resetForm }) => {
                // Check if email is already in use before submitting (skip check if editing with same email)
                setShowError(false);
                setErrorMsg("");

                const isEditingWithSameEmail =
                    staffId &&
                    reportUserData?.reportUserDetail &&
                    values.reportUserDetail.emailAddress ===
                        reportUserData.reportUserDetail.emailAddress;

                if (!isEditingWithSameEmail) {
                    try {
                        const response = await checkEmail({
                            url: `/internal/reports-admin/is-email-in-use?email=${encodeURIComponent(
                                values.reportUserDetail.emailAddress
                            )}`,
                        });

                        if (response.data === true) {
                            setShowError(true);
                            setErrorMsg(
                                "This email is already associated with an existing report user. Please use a different email address."
                            );
                            return; // Stop submission
                        }
                    } catch (error) {
                        console.error("Error checking email:", error);
                        // Continue with submission even if check fails
                    }
                }

                try {
                    let result;
                    if (staffId) {
                        result = await submitForm({
                            data: values,
                            method: "put",
                            url: `internal/reports-admin/user/${staffId}`,
                        });
                    } else {
                        result = await submitForm({
                            data: values,
                            method: "post",
                            url: `internal/reports-admin/user`,
                        });
                    }
                    if (!staffId) {
                        resetForm();
                        history.push(`/reporting/report-users/${result.data.staffId}/wizard`);
                    }
                    setShowSuccessfulSaveToast(true);
                    nextOnClick();
                } catch (error) {
                    setShowError(true);
                    setErrorMsg(
                        "An error occurred while saving. Please refresh the page and try again."
                    );
                }
            }}
        >
            {({ dirty }) => {
                return (
                    <>
                        <WizardModeIndicator paramId={staffIdFromParams} title="Report User" />
                        <ErrorComponent
                            errorMessage={errorMsg}
                            showError={showError}
                            setShowError={setShowError}
                        />
                        <Prompt
                            when={dirty && selectedKey != 2}
                            message="You have unsaved changes, are you sure you want to leave?"
                        />
                        <Pivot selectedKey={String(selectedKey)} onLinkClick={handleLinkClick}>
                            <PivotItem headerText="Basic Info" itemKey="0">
                                <BasicInfoStep staffId={staffId} />
                            </PivotItem>
                            {staffId && (
                                <PivotItem headerText="Access" itemKey="1">
                                    <AccessStep prevOnClick={prevOnClick} />
                                </PivotItem>
                            )}
                            {staffId && (
                                <PivotItem headerText="Access Roles" itemKey="2">
                                    <AccessRolesStep prevOnClick={prevOnClick} />
                                </PivotItem>
                            )}
                        </Pivot>
                        <Toast
                            show={showSuccessfulSaveToast}
                            message="Successfully Saved"
                            onClose={() => setShowSuccessfulSaveToast(false)}
                        />
                        <FeatureSummary
                            label="About Report Users"
                            summary="Create or edit report portal users (staff accounts). Configure basic info including name, email, position, and phone. Assign access levels to control which clients, cobrands, or cobrand tags the user can view. Set up role-based permissions for different report types."
                        />
                    </>
                );
            }}
        </Formik>
    );
};

export function mapInitialValues(reportUserData) {
    if (reportUserData) {
        return {
            reportUserDetail: {
                staffId: reportUserData.reportUserDetail?.staffId || null,
                accessLevel: reportUserData.reportUserDetail?.accessLevel || null,
                cobrandId: reportUserData.reportUserDetail?.cobrandId || null,
                clientId: reportUserData.reportUserDetail?.clientId || null,
                clientSchoolID: reportUserData.reportUserDetail?.clientSchoolID || null,
                coBrandTagID: reportUserData.reportUserDetail?.coBrandTagID || null,
                firstName: reportUserData.reportUserDetail?.firstName || "",
                lastName: reportUserData.reportUserDetail?.lastName || "",
                emailAddress: reportUserData.reportUserDetail?.emailAddress || "",
                position: reportUserData.reportUserDetail?.position || "",
                phoneNumber: reportUserData.reportUserDetail?.phoneNumber || "",
                status: reportUserData.reportUserDetail?.status?.trim() || "A",
            },
            reportUserRoles: reportUserData.reportUserRoles || [],
        };
    }
    return {
        reportUserDetail: {
            staffId: null,
            accessLevel: null,
            cobrandId: null,
            clientId: null,
            clientSchoolID: null,
            coBrandTagID: null,
            firstName: "",
            lastName: "",
            emailAddress: "",
            position: "",
            phoneNumber: "",
            status: "A",
        },
        reportUserRoles: [],
    };
}

export default ReportUsersWizardPage;
